'use client';

import { useShallow } from 'zustand/shallow';

import { findSkuForVariantChange, isVariantInStock, mapVariantGroups } from '@libs/shared-ui';

import { useSelectedDeliveryMethodStore } from '@/entities/delivery-method';
import type { ProductData, VariantKey, VariantValues as VariantValuesType } from '@/entities/product';
import { useProduct, useSelectedProductStore } from '@/entities/product';

export type UniqueVariantValues = NonNullable<ProductData['uniqueVariantValues']>;
export type VariantValues = NonNullable<VariantValuesType>;
export type VariantToSkuMap = Nullable<ProductData['variantToSkuMap']>;

export const useVariants = () => {
    const setSku = useSelectedProductStore(state => state.setSku);
    const { product, base, tailored } = useProduct();
    const method = useSelectedDeliveryMethodStore(useShallow(state => state.methodUpper));

    const variantToSkuMap = base?.variantToSkuMap;
    const activeVariants = product?.variantValues ?? {};

    const handleVariantChange = ({ group, value }: { group: VariantKey; value: string }) => {
        if (!variantToSkuMap) return;

        const newSku = findSkuForVariantChange({
            activeVariants,
            group,
            isSkuAvailable: sku =>
                isVariantInStock({
                    sku,
                    product: base?.variants[sku],
                    method,
                    tailored: tailored ?? undefined,
                }),
            value,
            variantToSkuMap,
        });

        if (newSku) setSku(newSku);
    };

    const variantGroups = mapVariantGroups({
        activeVariants,
        method,
        tailored,
        uniqueVariantValues: base?.uniqueVariantValues,
        variantToSkuMap: base?.variantToSkuMap,
        variants: base?.variants ?? {},
    });

    return {
        variantGroups,
        handleVariantChange,
    };
};
